/* RESPONSIBLE TEAM: team-standalone */
import { inject as service } from '@ember/service';
import IntercomRoute from 'embercom/routes/base/intercom-route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import type GreatGuidanceService from 'embercom/services/great-guidance-service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type Session from 'embercom/services/session';
import type Controller from '@ember/controller';
import type Store from '@ember-data/store';
import type SuiteMigrationService from 'embercom/services/suite-migration-service';
import { isReactRoute } from 'embercom/lib/react-route';

export default class StandaloneRoute extends IntercomRoute {
  @service declare appService: {
    app: {
      canUseStandalone: boolean;
      id: string;
      is_standalone_app: boolean;
      allowAccessOutsideStandaloneSection: boolean;
      canStandalonePlatformAccessReactIntegrationPages: boolean;
    };
  };
  @service declare session: Session;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare greatGuidanceService: GreatGuidanceService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare suiteMigrationService: SuiteMigrationService;

  get app() {
    return this.appService.app;
  }

  get appsController() {
    return this.controllerFor('apps/app') as Controller & { showPrimaryNav: boolean };
  }

  get applicationController() {
    return this.controllerFor('application') as Controller & {
      suite: string | null;
      standalone: string | null;
    };
  }

  activate() {
    this.applicationController.set('suite', null);

    // Set the standalone query parameter to null since we're in the /standalone/ route we don't need a special flag
    this.applicationController.set('standalone', null);
  }

  async beforeModel(transition: Transition) {
    this.suiteMigrationService.setForceSuiteVersion(false);

    let reactRoute = isReactRoute(transition.to);
    let platformIsUsingReact = this.app.canStandalonePlatformAccessReactIntegrationPages;
    let skipInitialization = platformIsUsingReact && reactRoute;

    if (!skipInitialization) {
      await this.finStandaloneService.initialize();
    }

    this.session.switchWorkspace(this.app.id);
    await Promise.all([
      this.session.getTeammate(this.app.id),
      this.session.workspace.fetchRequiredAttributes(),
    ]);

    if (transition.to.name === 'apps.app.standalone.index') {
      this.router.transitionTo('apps.app.standalone.reports');
    } else if (transition.to.name.startsWith('apps.app.standalone.setup')) {
      this.router.transitionTo('apps.app.standalone.deploy.overview');
    }

    this.appsController.set('showPrimaryNav', false);
  }

  redirect(): any {
    // This is intentionally distinct to checking the app.canUseStandalone flag as we may be transitioning from the Suite version of the app
    // where it would return false.
    if (!this.app.is_standalone_app) {
      return this.router.transitionTo('apps.app', this.app.id);
    }
  }
}
